import { useRef, useState, type ChangeEvent } from 'react'
import { CameraOutlined, EditOutlined, PictureOutlined } from '@ant-design/icons'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Alert, Button, Checkbox, Descriptions, Flex, Form, Input, Modal, Result, Segmented, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { formatBalanceBytes, formatDateTime, formatDuration } from '@/shared/lib/format'
import { QrScanner } from './QrScanner'
import {
  isCardLink,
  parseVoucherText,
  redeemVoucher,
  voucherRejectionKey,
  type EntryMethod,
  type VoucherCredential,
  type VoucherRedeemed,
} from './voucherApi'

type Method = 'code' | 'scan' | 'photo'

interface PendingCard {
  credential: VoucherCredential
  entryMethod: EntryMethod
}

function allowanceLabel(t: (key: string) => string, redeemed: VoucherRedeemed): string {
  const parts: string[] = []
  if (redeemed.quotaBytes !== null) parts.push(formatBalanceBytes(redeemed.quotaBytes))
  if (redeemed.quotaSeconds !== null) parts.push(formatDuration(redeemed.quotaSeconds))
  return parts.length > 0 ? parts.join(' · ') : t('common.unlimited')
}

export function VoucherRedeemModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const fileInput = useRef<HTMLInputElement>(null)
  const [method, setMethod] = useState<Method>('code')
  const [codeText, setCodeText] = useState('')
  const [readError, setReadError] = useState<string | null>(null)
  const [readingPhoto, setReadingPhoto] = useState(false)
  const [scanAttempt, setScanAttempt] = useState(0)
  const [pending, setPending] = useState<PendingCard | null>(null)
  const [acknowledged, setAcknowledged] = useState(false)

  const redeemMutation = useMutation({
    mutationFn: redeemVoucher,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.balance })
      void queryClient.invalidateQueries({ queryKey: queryKeys.usage })
      void queryClient.invalidateQueries({ queryKey: queryKeys.me })
    },
  })
  const redeemed = redeemMutation.data ?? null

  const resetInput = () => {
    setPending(null)
    setAcknowledged(false)
    setReadError(null)
    redeemMutation.reset()
  }

  const close = () => {
    if (redeemMutation.isPending) return
    resetInput()
    setMethod('code')
    setCodeText('')
    onClose()
  }

  const acceptText = (text: string, entryMethod: EntryMethod) => {
    const credential = parseVoucherText(text)
    if (!credential) {
      setReadError(t(entryMethod === 'CODE' ? 'voucher.codeInvalid' : 'voucher.qrNotCard'))
      return
    }
    setReadError(null)
    setAcknowledged(false)
    redeemMutation.reset()
    setPending({ credential, entryMethod })
  }

  const submitCode = () => acceptText(codeText, isCardLink(codeText) ? 'QR_LINK' : 'CODE')

  const readPhoto = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    setReadingPhoto(true)
    setReadError(null)
    try {
      const { decodeQrFromFile } = await import('./qrDecode')
      const text = await decodeQrFromFile(file)
      if (text) acceptText(text, 'QR_IMAGE')
      else setReadError(t('voucher.qrUnreadable'))
    } catch {
      setReadError(t('voucher.qrUnreadable'))
    } finally {
      setReadingPhoto(false)
    }
  }

  const changeMethod = (next: Method) => {
    setMethod(next)
    setReadError(null)
    setScanAttempt((n) => n + 1)
  }

  const changeCard = () => {
    resetInput()
    setScanAttempt((n) => n + 1)
  }

  const activate = () => {
    if (!pending || !acknowledged) return
    redeemMutation.mutate(pending)
  }

  const rejectionKey = redeemMutation.isError ? voucherRejectionKey(redeemMutation.error) : null
  const step = redeemed ? 'done' : pending ? 'confirm' : 'input'

  return (
    <Modal
      open={open}
      destroyOnClose
      title={t('voucher.title')}
      onCancel={close}
      onOk={step === 'done' ? close : step === 'confirm' ? activate : submitCode}
      okText={
        step === 'done'
          ? t('common.close')
          : step === 'confirm'
            ? redeemMutation.isPending
              ? t('voucher.activating')
              : t('voucher.activate')
            : t('voucher.continue')
      }
      confirmLoading={redeemMutation.isPending}
      okButtonProps={{
        hidden: step === 'input' && method !== 'code',
        disabled: (step === 'confirm' && !acknowledged) || (step === 'input' && codeText.trim() === ''),
      }}
      cancelButtonProps={{ hidden: step === 'done', disabled: redeemMutation.isPending }}
    >
      {step === 'done' && redeemed ? (
        <Result
          status="success"
          title={t('voucher.successTitle')}
          style={{ padding: '16px 0 0' }}
          extra={
            <Descriptions
              size="small"
              column={1}
              colon={false}
              items={[
                { key: 'plan', label: t('voucher.plan'), children: redeemed.planName ?? t('common.dash') },
                { key: 'size', label: t('voucher.size'), children: allowanceLabel(t, redeemed) },
                {
                  key: 'expires',
                  label: t('voucher.expires'),
                  children: redeemed.expiresAt ? formatDateTime(redeemed.expiresAt) : t('voucher.noExpiry'),
                },
              ]}
            />
          }
        />
      ) : step === 'confirm' && pending ? (
        <Flex vertical gap={12}>
          <Flex align="center" justify="space-between" gap={8} wrap>
            <Typography.Text strong>
              {'code' in pending.credential
                ? t('voucher.cardCode', { code: pending.credential.code })
                : t('voucher.qrCard')}
            </Typography.Text>
            <Button size="small" type="link" onClick={changeCard} disabled={redeemMutation.isPending}>
              {t('voucher.changeCard')}
            </Button>
          </Flex>
          <Alert
            type="warning"
            showIcon
            message={t('voucher.replaceTitle')}
            description={t('voucher.replaceText')}
          />
          <Checkbox
            checked={acknowledged}
            disabled={redeemMutation.isPending}
            onChange={(event) => setAcknowledged(event.target.checked)}
          >
            {t('voucher.acknowledge')}
          </Checkbox>
          {redeemMutation.isError ? (
            <Alert
              type={rejectionKey ? 'warning' : 'error'}
              showIcon
              message={rejectionKey ? t(rejectionKey) : translateError(redeemMutation.error, t)}
            />
          ) : null}
        </Flex>
      ) : (
        <Flex vertical gap={16}>
          <Segmented<Method>
            block
            value={method}
            onChange={changeMethod}
            options={[
              { value: 'code', label: t('voucher.methodCode'), icon: <EditOutlined /> },
              { value: 'scan', label: t('voucher.methodScan'), icon: <CameraOutlined /> },
              { value: 'photo', label: t('voucher.methodPhoto'), icon: <PictureOutlined /> },
            ]}
          />
          {method === 'code' ? (
            <Form layout="vertical">
              <Form.Item label={t('voucher.codeLabel')} style={{ marginBottom: 0 }}>
                <Input
                  autoFocus
                  size="large"
                  value={codeText}
                  autoComplete="off"
                  spellCheck={false}
                  placeholder={t('voucher.codePlaceholder')}
                  onChange={(event) => setCodeText(event.target.value)}
                  onPressEnter={submitCode}
                />
              </Form.Item>
            </Form>
          ) : method === 'scan' ? (
            <QrScanner key={scanAttempt} onDecoded={(text) => acceptText(text, 'QR_SCAN')} />
          ) : (
            <Flex vertical gap={12} align="flex-start">
              <Typography.Text type="secondary">{t('voucher.photoHint')}</Typography.Text>
              <input
                ref={fileInput}
                type="file"
                accept="image/*"
                capture="environment"
                hidden
                onChange={(event) => void readPhoto(event)}
              />
              <Button
                icon={<PictureOutlined />}
                loading={readingPhoto}
                onClick={() => fileInput.current?.click()}
              >
                {readingPhoto ? t('voucher.photoReading') : t('voucher.photoChoose')}
              </Button>
            </Flex>
          )}
          {readError ? (
            <Flex vertical gap={8} align="flex-start">
              <Alert type="warning" showIcon message={readError} style={{ alignSelf: 'stretch' }} />
              {method === 'scan' ? (
                <Button size="small" onClick={() => changeMethod('scan')}>
                  {t('voucher.scanAgain')}
                </Button>
              ) : null}
            </Flex>
          ) : null}
        </Flex>
      )}
    </Modal>
  )
}
